/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback, useMemo } from 'react';

import Imgix from 'js/components/Imgix';

import { Divider, MenuList } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import type {
  DegreeForProgramSwitcher as Degree,
  ThirdPartyOrganizationForProgramSwitcher as ThirdPartyOrganization,
} from 'bundles/composable-page-header/components/program-switcher/types';
import OrganizationCard, {
  ORG_SWITCHER_LOGO_WIDTH,
} from 'bundles/composable-page-header/components/user-experience-switcher/OrganizationCard';
import { sortDegreesByLaunchedDate } from 'bundles/composable-page-header/utils/sortByLaunchedAt';
import EnterpriseLogo from 'bundles/page-header/components/EnterpriseLogo';
import { fillWithWhiteBackground } from 'bundles/page/common/imgixParams';
import { useUpdateUserExperience } from 'bundles/unified-common/contexts/UserExperienceContext';

import CourseraLogo from 'images/page-header/coursera-logo-c-mark.png';

import _t from 'i18n!nls/composable-page-header';

export type Props = {
  currentOrganization?: ThirdPartyOrganization | null;
  currentDegree?: Degree | null;
  degrees?: Degree[] | null;
  organizations?: ThirdPartyOrganization[] | null;
};

const styles = {
  linkItem: css`
    &:focus,
    &:hover {
      text-decoration: none;
    }
  `,
  userExperienceSwitcherLink: (isCurrent: boolean) => css`
    margin-bottom: var(--cds-spacing-100);
    background-color: ${isCurrent ? 'var(--cds-color-grey-25)' : 'transparent'};

    :hover {
      text-decoration: none;
      background-color: var(--cds-color-grey-25);
    }
  `,
  organization: css`
    list-style: none;
    margin-top: var(--cds-spacing-100);
  `,
  divider: css`
    margin: var(--cds-spacing-200) 0;
  `,
};

const OrganizationList = ({ organizations, degrees, currentDegree, currentOrganization }: Props) => {
  const sortedDegrees = useMemo(() => sortDegreesByLaunchedDate(degrees ?? []), [degrees]);
  const { update: updateUserExperience } = useUpdateUserExperience();

  const track = useTracker();

  const onHomepageClick = useCallback<
    (homepageType: 'degree' | 'third_party_organization' | 'consumer', productId?: string) => void
  >((homepageType, productId) => {
    const previousHomepage = currentDegree?.id || currentOrganization?.id || '';
    let previousHomepageType: 'degree' | 'third_party_organization' | 'consumer' = 'consumer';
    if (currentDegree?.id) {
      previousHomepageType = 'degree';
    } else if (currentOrganization?.id) {
      previousHomepageType = 'third_party_organization';
    }

    track('switch_homepage', {
      previousHomepage,
      currentHomepage: productId || '',
      currentHomepageType: homepageType,
      previousHomepageType,
    });

    if (homepageType === 'consumer') {
      updateUserExperience({ selection: 'consumer' });
    } else if (homepageType === 'degree') {
      updateUserExperience({ selection: 'degree', degreeProgramId: productId });
    } else if (homepageType === 'third_party_organization') {
      updateUserExperience({ selection: 'enterprise', organizationId: productId });
    }
    // this function does not need to re-run when the dependencies ('currentDegree' and 'currentOrganization') change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // only show experience switcher for learners enrolled in multiple organizations or 1 or more degrees
  if ((organizations?.length ?? 0) < 2 && !degrees?.length) return null;

  return (
    <React.Fragment>
      <MenuList.ItemLink
        role="menuitem"
        key="personal-coursera-account"
        css={[styles.linkItem, styles.userExperienceSwitcherLink(!currentDegree && !currentOrganization)]}
        href="/"
        onClick={() => onHomepageClick('consumer')}
      >
        <OrganizationCard
          name={_t('Coursera Individual Learning')}
          description={_t('Personal Account')}
          isCurrent={!currentDegree && !currentOrganization}
          logo={<Imgix src={CourseraLogo} maxWidth={ORG_SWITCHER_LOGO_WIDTH} alt={_t('Coursera logo')} />}
        />
      </MenuList.ItemLink>
      {organizations?.map((organization) => {
        const { name: orgName, slug: orgSlug, id: orgId } = organization;
        const orgHomeHref = `/organizations/${orgSlug}`;
        const isCurrent = orgId === currentOrganization?.id;

        return (
          <MenuList.ItemLink
            role="menuitem"
            key={orgId}
            css={[styles.linkItem, styles.userExperienceSwitcherLink(isCurrent)]}
            href={orgHomeHref}
            onClick={() => onHomepageClick('third_party_organization', orgId)}
          >
            <OrganizationCard
              name={_t("#{orgName}'s Internal Learning", { orgName })}
              description={_t('Managed by #{orgName}', { orgName })}
              isCurrent={isCurrent}
              logo={
                <EnterpriseLogo
                  preferRectangle={false}
                  thirdPartyOrganization={organization}
                  logoWidth={ORG_SWITCHER_LOGO_WIDTH}
                />
              }
            />
          </MenuList.ItemLink>
        );
      })}
      {sortedDegrees?.map((degree) => {
        const { id: degreeId, name: degreeName, slug: degreeSlug, partners } = degree;

        const isCurrent = degreeId === currentDegree?.id;
        const degreeHref = `/degrees/${degreeSlug}/home`;
        const partnerSquareLogo = partners?.[0]?.squareLogo;

        return (
          <MenuList.ItemLink
            role="menuitem"
            key={degreeId}
            href={degreeHref}
            css={[styles.linkItem, styles.userExperienceSwitcherLink(isCurrent)]}
            onClick={() => onHomepageClick('degree', degreeId)}
          >
            <OrganizationCard
              name={degreeName}
              description={_t('Degree')}
              isCurrent={isCurrent}
              logo={
                partnerSquareLogo ? (
                  <Imgix
                    src={partnerSquareLogo}
                    maxWidth={ORG_SWITCHER_LOGO_WIDTH}
                    maxHeight={ORG_SWITCHER_LOGO_WIDTH}
                    alt={_t('Logo for #{degreeName}', { degreeName })}
                    imgParams={fillWithWhiteBackground}
                  />
                ) : undefined
              }
            />
          </MenuList.ItemLink>
        );
      })}
      <Divider css={styles.divider} />
    </React.Fragment>
  );
};

export default OrganizationList;
